import {
  isProductRefusalReason,
  type ProductRefusalDetails,
  type ProductRefusalReason,
} from "@lemonadetraining/contracts";

export type CallableError = Error & { code?: string };

// httpsCallable surfaces "functions/<code>"; some unit-test fixtures and
// pre-v9 SDK builds use the bare form. Accept both.
function hasCallableCode(error: unknown, code: string): boolean {
  const c = (error as CallableError | null)?.code;
  return c === code || c === "functions/" + code;
}

export const isPermissionDenied = (error: unknown) => hasCallableCode(error, "permission-denied");
export const isResourceExhausted = (error: unknown) => hasCallableCode(error, "resource-exhausted");

/** A failure that trying again can fix: the server stopped partway or was not reached. */
export const isRetryable = (error: unknown) => {
  const code = callableCode(error);
  return (
    code === null ||
    ["internal", "unknown", "unavailable", "deadline-exceeded", "resource-exhausted"].includes(code)
  );
};

/** The bare code, with the SDK's "functions/" prefix removed. */
export const callableCode = (error: unknown): string | null => {
  const code = (error as CallableError | null)?.code;
  if (typeof code !== "string") return null;
  return code.startsWith("functions/") ? code.slice("functions/".length) : code;
};

// The codes our callables throw with a sentence written for the reader. Any other
// code ("internal", "unavailable", ...) carries an SDK string, never shown.
const AUTHORED_CODES = [
  "invalid-argument",
  "failed-precondition",
  "already-exists",
  "permission-denied",
  "not-found",
  "resource-exhausted",
];

/* Several of these callables answer with a sentence already written for the
 * person reading it, so the caller can show it rather than inventing a worse
 * one. "" for any other callable code, so the caller falls back to its own plain
 * copy. An error with no code did not come from a callable and keeps its message. */
export const callableMessage = (error: unknown): string => {
  const code = callableCode(error);
  if (code !== null && !AUTHORED_CODES.includes(code)) return "";
  const message = (error as CallableError | null)?.message;
  return typeof message === "string" ? message.trim() : "";
};

/** The server's refusal reason (contracts `ProductRefusalReason`); null for anything else. */
export const refusalReason = (error: unknown): ProductRefusalReason | null => {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  return isProductRefusalReason(reason) ? reason : null;
};

/** How many members are on a commitment, when the refusal says. */
export const refusalCommitmentCount = (error: unknown): number | null => {
  const count = (error as { details?: Partial<ProductRefusalDetails> } | null)?.details
    ?.commitmentSubscriptionCount;
  return typeof count === "number" ? count : null;
};
